/**
 * Storefront-/locale-aware low-level formatting helpers.
 *
 * Kept deliberately tiny and list-driven so a new storefront/locale is a
 * one-line change instead of another inline ternary in a component.
 * CH stays byte-identical (apostrophe grouping, de-CH dates).
 */

/** Thousands grouping: CH uses ’, every other storefront uses a dot. */
export function groupIntFor(sf: string, n: number): string {
  const sep = sf === 'ch' ? '\u2019' : '.';
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, sep);
}

const DATE_LOCALES: Record<string, string> = {
  sq: 'sq-AL',
  it: 'it-IT',
  en: 'en-GB',
  fr: 'fr-CH',
};

/** Intl date locale for a UI locale. Defaults to de-CH (historical behaviour). */
export function dateLocaleFor(locale: string): string {
  return DATE_LOCALES[locale] ?? 'de-CH';
}

/**
 * Long date ("16 tetor 2025" style) for locales that Intl may not ship with a
 * proper CLDR pack. Albanian is spelled out explicitly so AL never falls back
 * to English month names; every other locale goes through Intl.
 */
const SQ_MONTHS = [
  'janar', 'shkurt', 'mars', 'prill', 'maj', 'qershor',
  'korrik', 'gusht', 'shtator', 'tetor', 'nëntor', 'dhjetor',
];

export function formatLongDate(value: string | Date, locale: string): string {
  const d = value instanceof Date ? value : new Date(value);
  if (locale === 'sq') {
    return `${String(d.getDate()).padStart(2, '0')} ${SQ_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  }
  return d.toLocaleDateString(dateLocaleFor(locale), { day: '2-digit', month: 'long', year: 'numeric' });
}
